import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Sorter } from 'src/utils/filters/sorters';
import React from 'react';
import StyledFilledSelect from '../Filters/StyledFilledSelect';
import {
  RadioButtonCheckedOutlined,
  RadioButtonUncheckedOutlined,
} from '@mui/icons-material';
import { Theme } from '@mui/material/styles';

interface Props {
  sorters: Sorter[];
  selectedSorter: Sorter;
  setSelectedSorter: React.Dispatch<React.SetStateAction<Sorter>>;
}
const SortersDropdown: React.FC<Props> = ({
  sorters,
  selectedSorter,
  setSelectedSorter,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );

  const [dropdownIsOpen, setDropdownIsOpen] = React.useState(false);
  const openDropdown = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropdownIsOpen(true);
  };
  const closeDropdown = () => {
    setDropdownIsOpen(false);
  };

  const resetSelectedSorter = () => setSelectedSorter(sorters[0]);

  const label = 'Sort by';

  return (
    <React.Fragment>
      <StyledFilledSelect
        displayEmpty
        // When on mobile, prevent the Select from opening its Menu and use Drawer instead
        open={!isSmallScreen && dropdownIsOpen}
        onOpen={openDropdown}
        onClose={closeDropdown}
        value={selectedSorter.displayName}
        sx={
          !isSmallScreen
            ? {
                background: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                },
                '.MuiSvgIcon-root ': {
                  fill: 'rgba(0, 0, 0, 0.54)',
                },
              }
            : {}
        }
        label={label}
        renderValue={() => {
          return (
            <Box
              sx={
                isSmallScreen
                  ? { color: theme.palette.primary.contrastText }
                  : {}
              }
            >
              <Box
                component="span"
                sx={!isSmallScreen ? { color: '#6B7280' } : {}}
              >
                Sort by
              </Box>{' '}
              {selectedSorter.displayName}
            </Box>
          );
        }}
      >
        {/* Desktop/tablet dropdown items */}
        {sorters.map((sorter) => (
          <MenuItem
            key={sorter.displayName}
            disableRipple
            value={sorter.displayName}
            sx={{
              padding: '0 9px',
              '&.Mui-selected, &.Mui-selected.Mui-focusVisible': {
                backgroundColor: 'transparent',
              },
              '&.Mui-selected:hover, &.Mui-selected:hover.Mui-selected.Mui-focusVisible':
                {
                  backgroundColor: theme.palette.action.hover,
                },
            }}
            onClick={() => setSelectedSorter(sorter)}
          >
            <Checkbox
              icon={<RadioButtonUncheckedOutlined />}
              checkedIcon={<RadioButtonCheckedOutlined />}
              disableRipple
              sx={{ paddingLeft: 0 }}
              checked={selectedSorter.displayName === sorter.displayName}
            />
            <ListItemText
              primary={sorter.displayName}
              slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
            />
          </MenuItem>
        ))}
        <Box sx={{ padding: '9px' }}>
          <Button variant="outlined" onClick={resetSelectedSorter}>
            Reset
          </Button>
        </Box>
      </StyledFilledSelect>

      {/* Mobile Drawer: acts as the Select menu on small screens */}
      <Drawer
        anchor="bottom"
        open={isSmallScreen && dropdownIsOpen}
        onClose={closeDropdown}
        ModalProps={{ keepMounted: true }}
        slotProps={{
          paper: {
            style: { height: 'auto', maxHeight: '85%' },
            sx: {
              borderTopLeftRadius: 10,
              borderTopRightRadius: 10,
            },
          },
        }}
      >
        <Box sx={{ p: '16px 24px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ px: 1, py: 0.5 }}>
          <List disablePadding sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {sorters.map((sorter) => {
              return (
                <ListItemButton
                  key={sorter.displayName}
                  onClick={() => {
                    setSelectedSorter(sorter);
                    closeDropdown();
                  }}
                  dense
                  sx={{ py: 0.5 }}
                >
                  <Checkbox
                    edge="start"
                    icon={<RadioButtonUncheckedOutlined />}
                    checkedIcon={<RadioButtonCheckedOutlined />}
                    disableRipple
                    sx={{ paddingLeft: 0 }}
                    checked={selectedSorter.displayName === sorter.displayName}
                  />
                  <ListItemText
                    primary={sorter.displayName}
                    slotProps={{ primary: { sx: { fontSize: '0.875rem' } } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
        <Divider />
        <Box
          sx={{
            p: 2,
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={resetSelectedSorter}>
            Reset
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={closeDropdown}>
              Done
            </Button>
          </Box>
        </Box>
      </Drawer>
    </React.Fragment>
  );
};

export default SortersDropdown;
